import { ApiError } from '@/lib/api';

/**
 * A distinção entre **recusa** e **erro**, do lado da tela.
 *
 * É a mesma linha que `packages/workflow/src/execute.ts` traça, e ela existe
 * porque as duas coisas pedem interfaces diferentes:
 *
 *   - **Recusa** é situação normal de negócio — o guard barrou, faltou campo,
 *     faltou justificativa. Tem motivo legível, escrito por quem desenhou o
 *     fluxo, e o que a pessoa precisa é *ler o motivo e resolver*. Não é
 *     vermelho, não some sozinha e não oferece "tentar de novo": tentar de
 *     novo daria exatamente a mesma resposta.
 *
 *   - **Conflito** é corrida — outra pessoa moveu o chamado enquanto esta tela
 *     estava aberta. O motivo é irrelevante; o que resolve é **recarregar**, e
 *     é isso que a tela oferece.
 *
 *   - **Permissão** é a pessoa, não o chamado. Nada que ela faça nesta tela
 *     resolve; o caminho é falar com quem administra.
 *
 *   - **Erro** é bug ou indisponibilidade. Aí sim vermelho, `requestId` à
 *     vista e botão de tentar de novo — porque tentar de novo pode funcionar.
 *
 * O mapa de códigos é o do servidor (`refusalToHttp`, em
 * `workflow-execution.service.ts`): `PERMISSION_DENIED` → 403,
 * `NOT_APPLICABLE` → 409, o resto das recusas → 422.
 *
 * ## O que se perde no caminho, e por que não se inventa aqui
 *
 * O 422 do servidor carrega `reason` e `missingFields` no corpo, mas o filtro
 * global (`DomainExceptionFilter`) devolve só `statusCode`, `message` e
 * `requestId`. A tela trabalha com a mensagem — que é a frase configurada no
 * guard, e a informação que de fato importa — e obtém os campos obrigatórios
 * de `GET /tickets/:id/transitions`, que já os declara antes de a pessoa
 * clicar.
 */

export type TipoDeFalha = 'recusa' | 'conflito' | 'permissao' | 'erro';

export interface Falha {
  tipo: TipoDeFalha;
  /** Em português, pronta para a tela. */
  mensagem: string;
  /** Só em `erro`: é o que permite achar a ocorrência no log do servidor. */
  requestId?: string;
}

const GENERICA = 'Não foi possível concluir a operação.';

export function classificarFalha(erro: unknown): Falha {
  if (erro instanceof ApiError) {
    if (erro.status === 422) return { tipo: 'recusa', mensagem: erro.message };
    if (erro.status === 409) return { tipo: 'conflito', mensagem: erro.message };
    if (erro.status === 403) return { tipo: 'permissao', mensagem: erro.message };
    return {
      tipo: 'erro',
      mensagem: erro.message,
      ...(erro.requestId ? { requestId: erro.requestId } : {}),
    };
  }
  // Falha de rede, JSON quebrado, bug na própria tela: nada disso é decisão de
  // negócio, e tratar como recusa esconderia um problema real atrás de uma
  // mensagem gentil.
  if (erro instanceof Error) return { tipo: 'erro', mensagem: erro.message };
  return { tipo: 'erro', mensagem: GENERICA };
}

/** Uma recusa não vira alarme; um erro, sim. */
export const ehRecusa = (falha: Falha): boolean => falha.tipo !== 'erro';
